/**
 * StudioDashboardView — the dashboard grid, transcribed from the approved 113
 * rebuild mock (`.design-shots/redesign-2026-09-16/dashboard.html`, frames 1,
 * 3, 4, 11).  See docs/app-map/studio/build/dashboard.md.
 *
 * This is not a restyle of the classic grid: the markup is the mock's markup
 * and the styling is the mock kit's own rules, ported into studio.css §20 with
 * an `st-db-` prefix (its own namespace, so §16's shared `st-` primitives do
 * not move).  All state and every handler are passed in from Dashboard.js as
 * `vm`, so behaviour, fetching and `data-testid` values are unchanged.
 */
import React from 'react';
import {
  Calendar, MessageCircle, Sparkles, Heart, UserPlus, LayoutGrid, Link2, Users,
  Plus, Check, CheckCircle2, Music, Lightbulb, Send, Play, GraduationCap,
  Download, Settings, ExternalLink, Copy, Repeat, MessageSquare,
} from 'lucide-react';
import { SubtaskChecklist } from '@/components/shared/Subtasks';

const initials = (name) => (name || '?')
  .split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/** The mock draws priority as a 6px dot: red high, amber medium, otherwise the control line. */
const priorityDot = (priority) => {
  if (priority === 'high') return 'var(--st-bad)';
  if (priority === 'medium' || priority === 'med') return 'var(--st-warn)';
  return 'var(--st-line2)';
};

function Avatar({ name, url, size }) {
  return (
    <span className={`st-db-av${size ? ` st-db-${size}` : ''}`} aria-hidden="true">
      {url ? <img src={url} alt="" /> : initials(name)}
    </span>
  );
}

function Widget({ tile, icon, title, small, right, children, footer, testId, onOpen }) {
  return (
    /* §45 (owner 2026-09-24): `data-app` carries the widget's app id to the card
       so `.st-db-lnk` can take that app's hue, the way the classic ghost button
       does (`text-indigo-600` on the planner, `text-blue-600` on comms, …).
       It is NOT `data-tile`: §8 gives every `[data-tile]` element the 28px
       coloured tile geometry, which on a <section> would paint the whole card.
       A data attribute in a Studio-only file, so classic is untouched. */
    <section className="st-db-card" data-app={tile} data-testid={testId}>
      <div className="st-db-wh">
        {onOpen ? (
          <button type="button" className="st-db-wtitle" onClick={onOpen}>
            <span className={`st-db-tile st-db-tile-${tile}`} data-tile={tile}>{icon}</span>
            <h2 className="st-db-wt">{title}{small ? <small>{small}</small> : null}</h2>
          </button>
        ) : (
          <div className="st-db-wtitle" style={{ cursor: 'default' }}>
            <span className={`st-db-tile st-db-tile-${tile}`} data-tile={tile}>{icon}</span>
            <h2 className="st-db-wt">{title}{small ? <small>{small}</small> : null}</h2>
          </div>
        )}
        {right ? <div className="st-db-wr">{right}</div> : null}
      </div>
      {children}
      {footer ? <div className="st-db-wf">{footer}</div> : null}
    </section>
  );
}

export default function StudioDashboardView({ vm }) {
  const {
    // planner
    todayTasks, completingTaskId, toggleTask, handleEditTask, openAddTask, formatTime12h,
    sharedTasksNode, subtasksApi, expandedSubtasks, toggleSubtaskExpand,
    // comms
    recentComms, formatTimeAgo, stripHtml, avatarForComms, openComms, openCommsItem, openQuickCompose,
    unreadComms,
    // ai
    aiMessage, setAiMessage, openAi, askAi, aiConversations,
    // team building
    weekActions, featuredWorkshop, openWorkshop, downloadDevo, devoEnabled, devoDisabled,
    toggleDevoAutoPost, openDevoSettings, openTrainingLibrary, isAdmin, hasChurchSlug,
    // onboarding
    pipelineTotal, trackChips, onboardingCandidates, candidateRole, candidateStage,
    candidateProgress, openOnboarding, openCandidate, copyIntakeLink, copiedLink,
    // stats + links
    stats, openInvite, quickLinks, openLinkManager,
    // slot 6 · Planning Center
    pcLoading, pcWidget,
    // page
    verse, onOpenPlanner,
  } = vm;

  const openTasks = todayTasks.filter((t) => !t.completed || t.id === completingTaskId);
  const doneCount = todayTasks.filter((t) => t.completed).length;

  return (
    <main className="st-db-wrap">
      <div className="st-db-grid">

        {/* 1 · Planner */}
        <Widget
          tile="planner" onOpen={() => onOpenPlanner('day')} icon={<Calendar />} title="Planner"
          right={(
            <div className="st-db-segc" role="tablist">
              {/* D25 (120): the owner wants Day on the RIGHT in both the widget and the
                  planner header. The planner header is already Year·Month·Week·Day, so the
                  widget's order is reversed here rather than with CSS `flex-direction:
                  row-reverse`: a visual reversal would leave Tab traversal running
                  right-to-left. This file renders only when `isStudio` is true, so classic
                  (Dashboard.js:1962) is untouched, and every button keeps its own label,
                  `onClick` and `aria-selected`. */}
              {[['year', 'Year'], ['month', 'Month'], ['week', 'Week'], ['day', 'Day']].map(([view, label]) => (
                <button key={view} type="button" role="tab" aria-selected={view === 'day'}
                        className={view === 'day' ? 'st-db-on' : undefined}
                        onClick={() => onOpenPlanner(view)}>{label}</button>
              ))}
            </div>
          )}
          footer={<button type="button" className="st-db-lnk" onClick={() => onOpenPlanner('day')}>Open planner</button>}
        >
          <div className="st-db-wb">
            <div className="st-db-seclbl">
              Today<span />
              <button type="button" className="st-db-btn st-db-ghost st-db-sm" style={{ marginLeft: 'auto' }}
                      onClick={openAddTask} data-testid="dashboard-add-task-btn"><Plus />Add task</button>
            </div>
            {/* Same rules as the classic list: shared tasks scroll with personal
                ones, completed tasks leave the list (the one being ticked stays
                for its strike animation), and the list caps at 256px and scrolls. */}
            <div className="st-db-tasks" data-testid="dashboard-task-list" style={{ marginTop: -6 }}>
              {openTasks.length === 0 && !sharedTasksNode ? (
                <div className="st-db-empty">
                  <span className="st-db-glyph"><CheckCircle2 /></span>
                  <b>{doneCount > 0 ? 'All tasks completed! 🎉' : 'No tasks for today'}</b>
                  <span>{doneCount > 0 ? 'Great job!' : 'Click “Add task” to get started.'}</span>
                </div>
              ) : openTasks.map((task) => {
                const done = task.completed || task.id === completingTaskId;
                // D2 (121 §5): the sub-task chip is a control, not a label — the
                // same store, the same expand set and the same handler the classic
                // row uses (Dashboard.js:2064), so `subtask-progress-chip` and the
                // checklist below the row come back to parity.
                const st = subtasksApi ? subtasksApi.getForTask(task) : { items: [], checkedIds: [], taskKey: null };
                const expanded = !!expandedSubtasks && expandedSubtasks.has(task.id);
                return (
                  <React.Fragment key={task.id}>
                  <div className={`st-db-task${done ? ' st-db-done' : ''}`}
                       onClick={() => handleEditTask(task)}
                       data-testid={`dashboard-task-${task.id}`}>
                    <button type="button" className={`st-db-cb${done ? ' st-db-on' : ''}`}
                            aria-label={done ? 'Mark not done' : 'Mark done'}
                            onClick={(e) => { e.stopPropagation(); toggleTask(task.id, task.completed, { title: task.title }); }}>
                      {done ? <Check /> : null}
                    </button>
                    <span className="st-db-pd" style={{ background: priorityDot(task.priority) }} />
                    <span className="st-db-t">{task.title}</span>
                    {st.items.length > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="st-db-subch"
                        data-testid="subtask-progress-chip"
                        title={expanded ? 'Hide sub-tasks' : 'Show sub-tasks'}
                        onClick={(e) => { e.stopPropagation(); toggleSubtaskExpand(task.id); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleSubtaskExpand(task.id); } }}
                      >
                        {st.checkedIds.length}/{st.items.length}
                      </span>
                    )}
                    {/* D24 (120, owner re-review 2026-09-18): the meta group reads
                        time -> repeat marker -> Overdue, with Overdue holding the
                        right-hand column. Reordered in the DOM rather than with CSS
                        `order`, so reading order and visual order stay in step (the
                        same call as D25). This file renders only when `isStudio`, so
                        classic's order in Dashboard.js is untouched. */}
                    <span className="st-db-time">{task.time ? formatTime12h(task.time) : (done ? 'Done' : '')}</span>
                    {task.isOverdue ? <span className="st-db-over">Overdue</span> : null}
                    <span className="st-repslot">
                      {task.is_recurring || task.parent_task_id ? <Repeat className="st-db-rep" aria-label="Repeats" /> : null}
                    </span>
                  </div>
                  {expanded && st.items.length > 0 && (
                    <SubtaskChecklist
                      items={st.items}
                      checkedIds={st.checkedIds}
                      onToggle={(subId, checked) => subtasksApi.toggleCheck(st.taskKey, task.id, subId, checked)}
                    />
                  )}
                  </React.Fragment>
                );
              })}
              {sharedTasksNode}
            </div>
          </div>
        </Widget>

        {/* 2 · Team Comms */}
        <Widget
          tile="comms" onOpen={openComms} icon={<MessageCircle />} title="Team Comms"
          right={(
            <>
              {unreadComms > 0 && <span className="st-db-chip st-db-static"><b>{unreadComms}</b> unread</span>}
              <button type="button" className="st-db-ibtn st-db-sm"
                      aria-label="Quick message (Shift+M)" onClick={openQuickCompose}
                      data-testid="quick-compose-trigger"><Send /></button>
            </>
          )}
          footer={<button type="button" className="st-db-lnk" onClick={openComms}>Open Team Comms</button>}
        >
          <div className="st-db-wb">
            {recentComms.length === 0 ? (
              /* the classic widget's two sample rows, at 55 % (mock frame 4) */
              <div className="st-db-feed st-db-ghost">
                <div className="st-db-frow" style={{ cursor: 'default' }}>
                  <Avatar name="Sample Post" />
                  <div>
                    <div className="st-db-who">Sample post<span className="st-db-meta">Post</span></div>
                    <div className="st-db-snip">Latest posts will appear here</div>
                  </div>
                </div>
                <div className="st-db-frow" style={{ cursor: 'default' }}>
                  <Avatar name="Sample Message" />
                  <div>
                    <div className="st-db-who">Sample message<span className="st-db-meta">Message</span></div>
                    <div className="st-db-snip">Latest messages will appear here</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="st-db-feed">
                {recentComms.map((item) => (
                  <div className="st-db-frow" key={`${item.type}-${item.id}`} onClick={() => openCommsItem(item)}>
                    <Avatar name={item.author?.name} url={avatarForComms(item)} />
                    <div>
                      <div className="st-db-who">
                        {item.author?.name || 'Unknown'}
                        <span className="st-db-meta">
                          {item.type === 'message' ? 'Message' : 'Post'} · {formatTimeAgo(item.created_at)}
                        </span>
                      </div>
                      <div className="st-db-snip">
                        {stripHtml(item.content) || (item.media_urls?.length ? 'Image' : 'No content')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Widget>

        {/* 3 · Worship AI */}
        <Widget
          tile="ai" onOpen={() => openAi()} icon={<Sparkles />} title="Worship AI" testId="worship-ai-widget"
          footer={<button type="button" className="st-db-lnk" onClick={() => openAi()}>Open Worship AI</button>}
        >
          <div className="st-db-wb">
            <p style={{ margin: 0, color: 'var(--st-fg2)', fontSize: '14px', lineHeight: 1.5 }}>
              Ask about setlists, keys and transitions, or talk through a team problem.
            </p>
            {/* §45 (owner 2026-09-24): "the dashboard recent AI chats should be
                above, and then below should be the setlist builder and helper and
                text box and send". No conversations at all → no Recent block, not
                an empty line (the quiet empty, 111 §6). D18 (121 §5): a row opens
                the AI app **on that conversation** — the id travels
                openAi → handleOpenApp options → WorshipAIChat, which selects it
                with the same loader the sidebar click uses. */}
            {(aiConversations || []).length > 0 && (
              <div>
                <div className="st-db-lbl" style={{ marginBottom: 2 }}>Recent</div>
                {(aiConversations || []).map((c) => (
                  <button type="button" className="st-db-rec" key={c.id} onClick={() => openAi(null, c.id)}>
                    <MessageSquare />
                    <span className="st-db-t">{c.title}</span>
                    <span className="st-db-time">{formatTimeAgo(c.updated_at || c.created_at)}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="st-db-chips">
              <button type="button" className="st-db-chip st-db-clk" data-testid="setlist-ideas-btn"
                      onClick={() => openAi('I want help finding songs and calls to worship for my pastors sermon on [ENTER PASSAGE OR THEME HERE]')}>
                <Music />Setlist helper
              </button>
              <button type="button" className="st-db-chip st-db-clk" data-testid="team-email-btn"
                      onClick={() => openAi('I am having a problem in my ministry and need help solving it. My problem is this [DESCRIBE PROBLEM IN AS MUCH DETAIL AS YOU CAN]')}>
                <Lightbulb />Problem solver
              </button>
            </div>
            <div className="st-db-ask">
              <input data-testid="ai-quick-input" placeholder="Ask anything…" value={aiMessage}
                     onChange={(e) => setAiMessage(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); askAi(); } }} />
              <button type="button" className="st-db-go" data-testid="ai-quick-send-btn"
                      aria-label="Ask Worship AI" onClick={askAi}><Send /></button>
            </div>
          </div>
        </Widget>

        {/* 4 · Team Building */}
        <Widget
          tile="team" icon={<Heart />} title="Team Building"
          footer={hasChurchSlug ? (
            <button type="button" className="st-db-lnk" onClick={openTrainingLibrary}
                    data-testid="open-training-library-btn">Open training library</button>
          ) : null}
        >
          <div className="st-db-wb">
            <div>
              <div className="st-db-lbl" style={{ marginBottom: 2 }}><Users className="st-secicon" />This week&apos;s team action</div>
              {weekActions.map((action, i) => (
                <div key={i} className="st-db-action">{action}</div>
              ))}
            </div>
            {isAdmin && hasChurchSlug && (
              <button type="button" className="st-db-workshop" onClick={openWorkshop}
                      data-testid="featured-workshop-strip">
                {featuredWorkshop ? (
                  <>
                    <span className="st-db-thumb">
                      {featuredWorkshop.thumbnail_url ? <img src={featuredWorkshop.thumbnail_url} alt="" /> : null}
                      <Play fill="currentColor" />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div className="st-db-eb">{featuredWorkshop.quarter_label || 'This quarter'} · Team workshop</div>
                      <b className="st-db-tt">{featuredWorkshop.title}</b>
                      {featuredWorkshop.description ? <span className="st-db-ss">{featuredWorkshop.description}</span> : null}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="st-db-cover"><GraduationCap /></span>
                    <div style={{ minWidth: 0 }}>
                      <div className="st-db-eb">Coming soon</div>
                      <b className="st-db-tt">Quarterly team workshop</b>
                    </div>
                  </>
                )}
              </button>
            )}
            <button type="button" className="st-db-btn st-db-pri st-db-full" onClick={downloadDevo}>
              <Download />Download this week&apos;s devo
            </button>
            <div className="st-db-trow">
              <span>Auto-post devos to the team feed</span>
              <span className="st-db-sp" />
              <button type="button" className="st-db-ibtn st-db-sm"
                      aria-label="Devotion post settings" onClick={openDevoSettings}
                      data-testid="devo-settings-btn"><Settings /></button>
              <button type="button" role="switch" aria-checked={!!devoEnabled} disabled={devoDisabled}
                      aria-label="Auto-post devos to the team feed"
                      className={`st-db-toggle${devoEnabled ? ' st-db-on' : ''}`}
                      onClick={() => toggleDevoAutoPost(!devoEnabled)}
                      data-testid="devo-autopost-switch" />
            </div>
          </div>
        </Widget>

        {/* 5 · Onboarding */}
        <Widget
          tile="onb" onOpen={openOnboarding} icon={<UserPlus />} title="Onboarding"
          right={onboardingCandidates.length > 0 ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pipelineTotal} in the pipeline</span> : null}
          footer={<button type="button" className="st-db-lnk" onClick={openOnboarding}>Open onboarding</button>}
        >
          <div className="st-db-wb">
            {onboardingCandidates.length === 0 ? (
              <div className="st-db-empty">
                <span className="st-db-glyph"><UserPlus /></span>
                <b>No applicants yet</b>
                <span>Share your link to get started.</span>
              </div>
            ) : (
              <>
                {trackChips.length > 0 && (
                  <div className="st-db-chips" data-testid="onboarding-track-breakdown">
                    {trackChips.map((t) => (
                      /* O11 (120): a long track name must ellipsis instead of stretching
                         the chip past the row. `.st-clip` is the shared one-line clamp. */
                      <span className="st-db-chip st-db-static" key={t.id}><span className="st-clip">{t.name}</span> <b>{t.count}</b></span>
                    ))}
                  </div>
                )}
                <div>
                  {/* Parity fix (120, session A): classic renders `.slice(0, 2)`
                      (Dashboard.js:2468) while this view rendered all three the vm
                      supplies, because the 113 mock drew three. A list cap is
                      behaviour, and doc 105 / BUILD-CARD rule 1 name it explicitly —
                      classic wins over the mock. Restoring the cap also closes the
                      extra `onboarding-candidate-*` id the parity gate reported.
                      Raised as question 11 because the mock is otherwise the spec. */}
                  {onboardingCandidates.slice(0, 2).map((c) => (
                    <div className="st-db-cand" key={c.id} onClick={() => openCandidate(c.id)}
                         data-testid={`onboarding-candidate-${c.id}`}>
                      <Avatar name={c.name} url={c.avatar_url} size="s" />
                      <div className="st-db-nm"><b>{c.name}</b><span>{candidateRole(c)}</span></div>
                      <div className="st-db-bar"><i style={{ width: `${candidateProgress(c)}%` }} /></div>
                      <span className="st-db-st">{candidateStage(c)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <button type="button" className="st-db-share" onClick={copyIntakeLink}>
              <Link2 />{copiedLink ? 'Link copied' : 'Share the application link'}<Copy />
            </button>
          </div>
        </Widget>

        {/* 6 · Planning Center when it is connected, else Quick stats + Quick links.
            The condition and the loading skeleton are the classic ones. */}
        {pcLoading ? (
          <section className="st-db-card st-db-pcskel">
            <div className="st-db-wh">
              <span className="st-db-tile st-db-tile-pc animate-pulse" />
              <span className="st-db-wt animate-pulse" style={{ width: 120, height: 16, borderRadius: 4 }} />
            </div>
            <div className="st-db-wb">
              <span className="animate-pulse" style={{ height: 14, borderRadius: 4 }} />
              <span className="animate-pulse" style={{ height: 14, width: '75%', borderRadius: 4 }} />
              <span className="animate-pulse" style={{ height: 14, width: '50%', borderRadius: 4 }} />
            </div>
          </section>
        ) : pcWidget ? (
          <div className="st-db-pcw">{pcWidget}</div>
        ) : (
          <div className="st-db-split">
            <section className="st-db-card">
              <div className="st-db-wh">
                <span className="st-db-tile st-db-tile-pc" data-tile="pc"><LayoutGrid /></span>
                <h2 className="st-db-wt">Quick stats</h2>
              </div>
              <div className="st-db-wb">
                <div className="st-db-stats">
                  {stats.map((s) => (
                    s.onClick ? (
                      <button type="button" className="st-db-stat" key={s.label} onClick={s.onClick}
                              style={{ cursor: 'pointer' }}>
                        <b>{s.value}</b><span>{s.label}</span>
                      </button>
                    ) : (
                      <div className="st-db-stat" key={s.label}><b>{s.value}</b><span>{s.label}</span></div>
                    )
                  ))}
                </div>
                <button type="button" className="st-db-btn st-db-pri st-db-full" onClick={openInvite}
                        data-testid="invite-team-btn"><UserPlus />Invite team</button>
              </div>
            </section>

            <section className="st-db-card">
              <div className="st-db-wh">
                <span className="st-db-tile st-db-tile-pc" data-tile="pc"><Link2 /></span>
                <h2 className="st-db-wt">Quick links</h2>
                <div className="st-db-wr">
                  <button type="button" className="st-db-ibtn st-db-sm"
                          aria-label="Manage links" onClick={openLinkManager}><Settings /></button>
                </div>
              </div>
              <div className="st-db-wb" style={{ gap: 8 }}>
                {quickLinks.map((link) => (
                  <a className="st-db-qlink" key={link.id} href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.title}<ExternalLink />
                  </a>
                ))}
                {quickLinks.length < 5 && (
                  <button type="button" className="st-db-btn st-db-ghost st-db-sm" onClick={openLinkManager}
                          style={{ alignSelf: 'flex-start' }}><Plus />Add link</button>
                )}
              </div>
            </section>
          </div>
        )}
      </div>

      {verse ? (
        <p className="st-db-verse">
          &ldquo;{verse.verse.replace(/\n/g, ' ')}&rdquo;
          <b>{verse.reference}</b>
        </p>
      ) : null}
    </main>
  );
}
